/* Copyright (c) 2020-present CloudIO Inc */

import { page as SamplePage } from '../../help/sample_pages/ActionCreatePage';
import { Platform } from '../../platform/Platform';
import { ItemType } from '../../registry/Base.type';
import {
  ActionManager,
  ActionManagerFn,
  AliasType,
} from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionCreate';
import { analyzeActionCreate } from './ActionCreateAnalyze';

const ActionCreateManager: ActionManagerFn<'ActionCreate'> = (
  platform: Platform,
): ActionManager<'ActionCreate'> => {
  return {
    ...baseActionManager(platform, 'ActionCreate'),
    isChildAllowed: (page, item, childType) =>
      ['ActionSet'].includes(childType as ItemType),
    defaultValue,
    defaultChildren: ['ActionSet'],
    props: {
      ...baseActionProps,
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'TransientDataStore',
          'ReadOnlyDataStore',
        ],
      },
      addOnTop: {
        label: 'Add On Top',
        type: 'boolean',
        helpText:
          'Turn this off if you want the newly added row to be inserted to the bottom of all the existing records.',
      },
      markAsDirty: {
        label: 'Mark As Dirty',
        type: 'boolean',
        helpText:
          'If the newly added record is marked as dirty, then it will be posted to the db even if the record is not changed by the user. By default, all the newly added rows will not be posted to the db. Only the records that are changed (dirty) by the user will get posted.',
      },
    },
    name: 'Create Record',
    getDisplayText: (item) => item.props.alias,
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'STORE'
        ? { aliasProp: 'alias', embeddedPageIdProp: 'embeddedPageId' } // TODO: Add embeddedPageId prop
        : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    icon: { iconName: 'plus' },
    analyze: analyzeActionCreate,
    description: `**Overview**<br/>
The Action Set component is a precision assignment tool designed to populate or update specific attributes within a record. It functions strictly as a child component inside data manipulation contexts—such as creating new records or iterating through datasets—serving as the mechanism to define exactly what data is written to a specific field.<br/><br/>
This component incorporates dynamic type-checking logic. It derives the expected data type from the selected target attribute (e.g., Number, Date, Boolean, JSON) and enforces that the assigned value expression matches that type, ensuring configuration-level data integrity.

**Key Features**<br/>
* **Strict Parent Scope:** Restricted via isAllowedInsideParent to supported data-handling containers such as ActionCreate, ActionDuplicateRecord, TriggerBeforeCreate, ActionCopyRecordInLoop, and ActionLoopRecords.
* **Dynamic Type Resolution:** Uses a helper (returnType) that reads the underlying store/attribute metadata and maps store-level types (such as YN/TF, Date, JSON) into the corresponding expression return types.
* **Sequential Configuration:** Hides the value input until an attribute is selected (isPropertyVisible), and resets the value when the attribute selection changes (onPropertyChange) to prevent stale assignments.
* **Context-Aware Validation:** Detects and surfaces configuration issues when the parent store context is missing or when the chosen attribute is not valid for the current context.

**Properties**<br/>
* **Attribute (Required):** A ViewAttributeChooser used to select the target field for assignment.
  * *Configuration:* aliasType: 'STORE', preventMultiEdit enabled.
* **Value (Required):** An expression defining the data to assign to the selected attribute.
  * *Validation:* returnType is dynamically calculated to match the selected attribute.
  * *Guidance:* Help text indicates the value must match the data type of the selected attribute.

**Use Cases**<br/>
* **Record Initialization:** Defining default values (e.g., Status = 'New', CreatedDate = Now()) when used inside an ActionCreate.
* **Batch Updates:** Modifying a field for every record processed when nested within an ActionLoopRecords (e.g., setting Processed = true for a list of items).`,

    helpRenders: [
      {
        title: 'Action Create Record',
        description:
          "Try changing the props and press the below 'Inovke Action' button to see it in action! Press the 'Reset' button to remove the newly added rows from the store.",
        page: SamplePage,
        height: 300,
        exampleId: 'e1',
        clearParentChildren: true,
        visibleProps: ['addOnTop'],
        item: {
          children: [],
          itemId: 'actionCreate',
          parent: 'triggerOnClick',
          props: {
            alias: 'test',
          },
          type: 'ActionCreate',
        },
      },
    ],
  };
};

export default ActionCreateManager;
